import React, { useEffect, useRef, useState } from 'react';
import {
	Box,
	Button,
	FormControl,
	FormLabel,
	Input,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalHeader,
	ModalOverlay,
	Stack,
	Text,
	Textarea,
	Tabs,
	TabList,
	TabPanels,
	Tab,
	TabPanel,
	Accordion,
	AccordionItem,
	AccordionButton,
	AccordionPanel,
	AccordionIcon,
} from '@chakra-ui/react';
import axios from 'axios';
import { PlayerSegment } from '@repo/alictus-common/types/segment';
import { FilterType, FilterCondition, Platform, RCType, RemoteConfigValue } from '@repo/alictus-common/types';
import { FilterOperation } from '@repo/alictus-common/types/runtime/filter_operation';
import config from '../config';
import globalStore from '../store';
import { useToast } from '@chakra-ui/react';

export function RemoteConfigReadModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
	const globalData = globalStore();
	const toast = useToast();
	const [segments, setSegments] = React.useState<PlayerSegment[]>([]);

	// Single ID mode state
	const [remoteConfigId, setRemoteConfigId] = useState('');
	const [remoteConfigValue, setRemoteConfigValue] = useState<RemoteConfigValue | null>(null);

	// Multiple IDs mode state
	const [remoteConfigIds, setRemoteConfigIds] = useState('');
	const [remoteConfigValues, setRemoteConfigValues] = useState<RemoteConfigValue[]>([]);

	// Player Segments mode state
	const [playerSegmentIds, setPlayerSegmentIds] = useState('');
	const [playerSegmentValues, setPlayerSegmentValues] = useState<PlayerSegment[]>([]);

	const [fetching, setFetching] = useState(false);
	const hasFetched = useRef(false);

	const resetResults = () => {
		setRemoteConfigValue(null);
		setRemoteConfigValues([]);
		setPlayerSegmentValues([]);
		hasFetched.current = false;
	};

	useEffect(() => {
		if (isOpen && segments.length === 0) {
			const fetchSegments = async () => {
				try {
					const segmentsResult = await axios.get(`${config.API_ENDPOINT}/game/${globalData.gameId}/segment`, {
						headers: { Authorization: `Bearer ${globalData.JWTToken}` },
					});
					setSegments(segmentsResult.data as PlayerSegment[]);
				} catch (error) {
					console.error('Failed to fetch segments:', error);
				}
			};

			fetchSegments();
		}
	}, [isOpen, globalData.gameId, globalData.JWTToken, segments.length]);

	async function fetchRemoteConfig() {
		if (!remoteConfigId) return;

		setFetching(true);
		try {
			const rcResult = await axios.get(`${config.API_ENDPOINT}/game/${globalData.gameId}/rc_package/1/rc_value/${remoteConfigId}`, {
				headers: { Authorization: `Bearer ${globalData.JWTToken}` },
			});
			setRemoteConfigValue(rcResult.data as RemoteConfigValue);
			setRemoteConfigValues([]); // Clear multiple values when fetching single

			hasFetched.current = true;
		} catch (err) {
			console.error('Failed to fetch remote config:', err);
			toast({
				title: 'Remote Config Not Found',
				description: `No Remote Config found with ID: ${remoteConfigId}`,
				status: 'error',
				duration: 5000,
				isClosable: true,
				position: 'top',
			});
			setRemoteConfigValue(null);
		}
		setFetching(false);
	}

	async function fetchMultipleRemoteConfigs() {
		if (!remoteConfigIds.trim()) return;

		setFetching(true);
		try {
			// Parse comma-separated IDs and clean them
			const idList = remoteConfigIds
				.split(',')
				.map((id) => id.trim())
				.filter((id) => id.length > 0);

			if (idList.length === 0) {
				toast({
					title: 'Invalid Input',
					description: 'Please enter at least one Remote Config ID',
					status: 'error',
					duration: 5000,
					isClosable: true,
					position: 'top',
				});
				setFetching(false);
				return;
			}

			const rcResult = await axios.post(
				`${config.API_ENDPOINT}/game/${globalData.gameId}/rc_package/rc_value/get_rc_from_id_list`,
				{ rcValueIds: remoteConfigIds },
				{
					headers: { Authorization: `Bearer ${globalData.JWTToken}` },
				},
			);

			console.log('Fetched Remote Configs:', rcResult.data);
			setRemoteConfigValues(rcResult.data as RemoteConfigValue[]);
			setRemoteConfigValue(null); // Clear single value when fetching multiple

			hasFetched.current = true;
		} catch (err) {
			console.error('Failed to fetch remote configs:', err);
			toast({
				title: 'Failed to Fetch Remote Configs',
				description: 'Some or all Remote Configs could not be found',
				status: 'error',
				duration: 5000,
				isClosable: true,
				position: 'top',
			});
			setRemoteConfigValues([]);
		}
		setFetching(false);
	}

	async function fetchMultiplePlayerSegments() {
		if (!playerSegmentIds.trim()) return;

		setFetching(true);
		try {
			// Parse comma-separated IDs and clean them
			const idList = playerSegmentIds
				.split(',')
				.map((id) => id.trim())
				.filter((id) => id.length > 0);

			if (idList.length === 0) {
				toast({
					title: 'Invalid Input',
					description: 'Please enter at least one Player Segment ID',
					status: 'error',
					duration: 5000,
					isClosable: true,
					position: 'top',
				});
				setFetching(false);
				return;
			}

			const segmentResult = await axios.post(
				`${config.API_ENDPOINT}/game/${globalData.gameId}/segment/player_segments_by_id_list`,
				{ segmentIds: playerSegmentIds },
				{
					headers: { Authorization: `Bearer ${globalData.JWTToken}` },
				},
			);

			console.log('Fetched Player Segments:', segmentResult.data);
			setPlayerSegmentValues(segmentResult.data as PlayerSegment[]);
			setRemoteConfigValue(null); // Clear other values when fetching segments
			setRemoteConfigValues([]);

			hasFetched.current = true;
		} catch (err) {
			console.error('Failed to fetch player segments:', err);
			toast({
				title: 'Failed to Fetch Player Segments',
				description: 'Some or all Player Segments could not be found',
				status: 'error',
				duration: 5000,
				isClosable: true,
				position: 'top',
			});
			setPlayerSegmentValues([]);
		}
		setFetching(false);
	}

	const renderSingleValue = (rcValue: RemoteConfigValue) => {
		const formattedValue =
			typeof rcValue.value === 'string' && rcValue.value.startsWith('{')
				? JSON.stringify(JSON.parse(rcValue.value), null, 2)
				: rcValue.value;

		const segmentFilter = (rcValue.filterOperations.find((filter) => filter.Filter === FilterType.Segmentation) as FilterOperation) || {
			Filter: FilterType.Segmentation,
			Condition: FilterCondition.NoOp,
			Values: [],
		};
		const countryFilter = (rcValue.filterOperations.find((filter) => filter.Filter === FilterType.Country) as FilterOperation) || {
			Filter: FilterType.Country,
			Condition: FilterCondition.NoOp,
			Values: [],
		};

		const versionFilters = rcValue.filterOperations.filter((f) => f.Filter === FilterType.Version);
		const buildNumberFilters = rcValue.filterOperations.filter((f) => f.Filter === FilterType.BuildNumber);

		const getSelectedCountries = (): string[] => {
			return countryFilter.Values ? countryFilter.Values.map((country) => country as string) : [];
		};

		const getSelectedSegments = (): string[] => {
			return segmentFilter.Values
				? segmentFilter.Values.map((segment) => {
						const segmentObj = segments.find((s) => s.uid === segment);
						return segmentObj ? segmentObj.name : (segment as string);
					})
				: [];
		};

		return (
			<Stack spacing={4} mt={4}>
				<Box>
					<FormLabel>Key</FormLabel>
					<Input value={rcValue.key} isReadOnly />
				</Box>
				<Box>
					<FormLabel>Description</FormLabel>
					<Textarea value={rcValue.description} isReadOnly />
				</Box>
				<Box>
					<FormLabel>Type</FormLabel>
					<Input value={RCType[rcValue.type]} isReadOnly />
				</Box>
				<Box>
					<FormLabel>Privacy Mode</FormLabel>
					<Input value={rcValue.isPersonal ? 'Private' : 'Public'} isReadOnly />
				</Box>
				<Box>
					<FormLabel>Environment</FormLabel>
					<Input value={rcValue.isTest ? 'Development' : 'Production'} isReadOnly />
				</Box>
				<Box>
					<FormLabel>Platform</FormLabel>
					<Input value={Platform[rcValue.platform]} isReadOnly />
				</Box>
				<Box>
					<FormLabel>Segment Filter</FormLabel>
					<Text>
						Condition: <b>{FilterCondition[segmentFilter.Condition]}</b>
					</Text>
					<Text>Segments: {getSelectedSegments().join(', ') || 'None'}</Text>
				</Box>
				<Box>
					<FormLabel>Country Filter</FormLabel>
					<Text>
						Condition: <b>{FilterCondition[countryFilter.Condition]}</b>
					</Text>
					<Text>Countries: {getSelectedCountries().join(', ') || 'None'}</Text>
				</Box>
				<Box>
					<FormLabel>Version Filters</FormLabel>
					{versionFilters.length === 0 ? (
						<Text>No version filters</Text>
					) : (
						versionFilters.map((filter, idx) => (
							<Text key={idx}>
								{FilterCondition[filter.Condition]}: {filter.Values.join(', ')}
							</Text>
						))
					)}
				</Box>
				<Box>
					<FormLabel>Build Number Filters</FormLabel>
					{buildNumberFilters.length === 0 ? (
						<Text>No build number filters</Text>
					) : (
						buildNumberFilters.map((filter, idx) => (
							<Text key={idx}>
								{FilterCondition[filter.Condition]}: {filter.Values.join(', ')}
							</Text>
						))
					)}
				</Box>
				<Box>
					<FormLabel>Value</FormLabel>
					<Textarea value={formattedValue} isReadOnly minH="200px" />
				</Box>
			</Stack>
		);
	};

	const renderValue = () => {
		if (remoteConfigValue) {
			return renderSingleValue(remoteConfigValue);
		}
		return null;
	};

	const renderMultipleValues = () => {
		if (remoteConfigValues.length === 0) return null;

		return (
			<Stack spacing={4} mt={4}>
				<Text fontWeight="bold">Found {remoteConfigValues.length} Remote Config(s)</Text>
				<Accordion allowMultiple>
					{remoteConfigValues.map((rcValue, index) => (
						<AccordionItem key={rcValue.id || index}>
							<AccordionButton>
								<Box flex="1" textAlign="left">
									<Text fontWeight="semibold">
										ID: {rcValue.id} - {rcValue.key}
									</Text>
									<Text fontSize="sm" color="gray.600">
										{rcValue.description}
									</Text>
								</Box>
								<AccordionIcon />
							</AccordionButton>
							<AccordionPanel pb={4}>{renderSingleValue(rcValue)}</AccordionPanel>
						</AccordionItem>
					))}
				</Accordion>
			</Stack>
		);
	};

	const renderSinglePlayerSegment = (segment: PlayerSegment) => {
		return (
			<Stack spacing={4} mt={4}>
				<Box>
					<FormLabel>Name</FormLabel>
					<Input value={segment.name} isReadOnly />
				</Box>
				<Box>
					<FormLabel>Description</FormLabel>
					<Textarea value={segment.description} isReadOnly />
				</Box>
				<Box>
					<FormLabel>UID</FormLabel>
					<Input value={segment.uid} isReadOnly />
				</Box>
				<Box>
					<FormLabel>Environment</FormLabel>
					<Input value={segment.isTest ? 'Development' : 'Production'} isReadOnly />
				</Box>
				<Box>
					<FormLabel>Mock Mode</FormLabel>
					<Input value={segment.isMock ? 'Yes' : 'No'} isReadOnly />
				</Box>
				<Box>
					<FormLabel>Creation Date</FormLabel>
					<Input value={new Date(segment.creationDate * 1000).toLocaleString()} isReadOnly />
				</Box>
				<Box>
					<FormLabel>Last Updated</FormLabel>
					<Input value={new Date(segment.lastUpdated * 1000).toLocaleString()} isReadOnly />
				</Box>
				<Box>
					<FormLabel>Filter Operations</FormLabel>
					<Textarea value={JSON.stringify(segment.filterOperations, null, 2)} isReadOnly minH="200px" />
				</Box>
			</Stack>
		);
	};

	const renderMultiplePlayerSegments = () => {
		if (playerSegmentValues.length === 0) return null;

		return (
			<Stack spacing={4} mt={4}>
				<Text fontWeight="bold">Found {playerSegmentValues.length} Player Segment(s)</Text>
				<Accordion allowMultiple>
					{playerSegmentValues.map((segment, index) => (
						<AccordionItem key={segment.id || index}>
							<AccordionButton>
								<Box flex="1" textAlign="left">
									<Text fontWeight="semibold">
										ID: {segment.id} - {segment.name}
									</Text>
									<Text fontSize="sm" color="gray.600">
										{segment.description}
									</Text>
								</Box>
								<AccordionIcon />
							</AccordionButton>
							<AccordionPanel pb={4}>{renderSinglePlayerSegment(segment)}</AccordionPanel>
						</AccordionItem>
					))}
				</Accordion>
			</Stack>
		);
	};

	return (
		<Modal isOpen={isOpen} onClose={onClose}>
			<ModalOverlay />
			<ModalContent maxW="700px">
				<ModalHeader>View Remote Config & Player Segments</ModalHeader>
				<ModalCloseButton />
				<ModalBody>
					<Tabs onChange={resetResults}>
						<TabList>
							<Tab>Single Remote Config</Tab>
							<Tab>Multiple Remote Configs</Tab>
							<Tab>Multiple Player Segments</Tab>
						</TabList>
						<TabPanels>
							<TabPanel>
								<Stack spacing={4}>
									<FormControl>
										<FormLabel>Remote Config ID</FormLabel>
										<Input
											placeholder="Enter Remote Config ID"
											value={remoteConfigId}
											onChange={(e) => setRemoteConfigId(e.target.value)}
										/>
									</FormControl>
									<Button colorScheme="blue" onClick={fetchRemoteConfig} isLoading={fetching}>
										Fetch Config
									</Button>
									{renderValue()}
								</Stack>
							</TabPanel>
							<TabPanel>
								<Stack spacing={4}>
									<FormControl>
										<FormLabel>Remote Config IDs</FormLabel>
										<Textarea
											placeholder="Enter comma-separated Remote Config IDs (e.g., 123, 456, 789)"
											value={remoteConfigIds}
											onChange={(e) => setRemoteConfigIds(e.target.value)}
											rows={3}
										/>
									</FormControl>
									<Button colorScheme="blue" onClick={fetchMultipleRemoteConfigs} isLoading={fetching}>
										Fetch Configs
									</Button>
									{renderMultipleValues()}
								</Stack>
							</TabPanel>
							<TabPanel>
								<Stack spacing={4}>
									<FormControl>
										<FormLabel>Player Segment IDs</FormLabel>
										<Textarea
											placeholder="Enter comma-separated Player Segment IDs (e.g., 123, 456, 789)"
											value={playerSegmentIds}
											onChange={(e) => setPlayerSegmentIds(e.target.value)}
											rows={3}
										/>
									</FormControl>
									<Button colorScheme="blue" onClick={fetchMultiplePlayerSegments} isLoading={fetching}>
										Fetch Player Segments
									</Button>
									{renderMultiplePlayerSegments()}
								</Stack>
							</TabPanel>
						</TabPanels>
					</Tabs>
				</ModalBody>
			</ModalContent>
		</Modal>
	);
}

export default RemoteConfigReadModal;
